// BESZÉLGETÉS-SOR (avatar + fejléc-meta + törzs) — a jegy-szálak KÖZÖS, megjelenítő komponense.
//
// MIÉRT KÖZÖS: a belső jegy-nézet és a partner-portál UGYANAZT a beszélgetést mutatja, csak más
// affordanciákkal (a belső szerkeszthet és belső jegyzetet is lát, a partner nem). A KERET viszont azonos:
// avatar · szerző · meta-sor · törzs — és a SAJÁT üzenet kiemelt tónussal. Két külön markup itt garantáltan
// elcsúszott volna.
//
// A komponens SEMMIT nem tud a doménről: se komment-fajtát, se jogot nem értelmez — azt a hívó dönti el, és a
// `meta`/`badges` slotokban adja át.
//
// AZ ALAK KÁRTYÁS (user-döntés): a teljes bejegyzés halvány szegélyt kap, a törzs a kártya bal szélétől indul
// (az avatar ALATT), és a hívó az akció-ikonjait a KÁRTYA SZEGÉLYÉRE ülteti (`absolute -bottom-3 right-4`).
import type { ReactNode } from 'react';

import { THREAD_TONE_OURS } from '@/components/shared/thread-tone';
import { Avatar } from '@/components/ui/avatar';
import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';

export interface ThreadMessageProps {
  authorName: string;
  authorGivenName?: string | null | undefined;
  authorFamilyName?: string | null | undefined;
  avatarUrl?: string | null | undefined;
  /** A kolléga csapat-színe — az avatar háttere (partnernél nincs). */
  accentColor?: string | null | undefined;
  /** A fejléc-sor jobb oldala (időpont, jelvények) — a hívó rendereli. */
  meta?: ReactNode;
  /** A szerző neve UTÁN jövő jelölések (pl. „új", „belső jegyzet"). */
  badges?: ReactNode;
  /** A SAJÁT (hívó által írt) üzenet kiemelt tónust kap — a szál így egy pillantásra olvasható. */
  mine?: boolean;
  /** Halványítás a küldés alatt (optimista sor). */
  pending?: boolean;
  children: ReactNode;
}

export function ThreadMessage({
  authorName,
  avatarUrl,
  accentColor,
  meta,
  badges,
  mine = false,
  pending = false,
  children,
}: ThreadMessageProps): JSX.Element {
  return (
    <div
      className={cn(
        // KÁRTYÁS SOR (user-döntés): a TELJES bejegyzés — avatar, név, meta és törzs együtt — halvány
        // szegélyt kap, és az AKCIÓ-IKONOK (emoji, szerkesztés) EZEN a szegélyen ülnek. Korábban a sorokat
        // hajszálvonal választotta el, a törzs pedig az avatar MELLETT, behúzva indult: az ikonok így egy
        // láthatatlan él fölött lógtak, és a „hol kezdődik az egyik bejegyzés, hol a másik" kérdést a
        // vonal önmagában nem válaszolta meg. A belső jegy-szál ugyanezt a kártyás alakot használja.
        'relative rounded-lg border p-3',
        // A SAJÁT bejegyzés tónusa MARAD (a szál egy pillantásra olvasható) — csak most a KÁRTYA hordozza,
        // nem egy külön buborék a törzs körül. A `brand` szegély halvány: a kiemelés a háttér dolga.
        mine ? THREAD_TONE_OURS : 'border-border bg-sunken/40',
        pending && 'opacity-60',
      )}
    >
      {/* FEJLÉC-SOR: avatar + név + jelölések + meta. A `md` (28px) avatar — user-mérés: a `lg` (36px) a
          rövid válaszoknál magasabb volt, mint a hozzá tartozó szöveg-blokk. */}
      <div className="mb-1.5 flex flex-wrap items-center gap-2">
        <Avatar name={authorName} src={avatarUrl ?? null} size="md" accentColor={accentColor} />
        <Text variant="itemTitle" as="span">
          {authorName}
        </Text>
        {badges}
        <span className="ml-auto">{meta}</span>
      </div>
      {/* A TÖRZS a kártya BAL SZÉLÉTŐL indul (az avatar ALATT), nem mellé behúzva (user-döntés): így a
          szöveg a teljes szélességet használja, és a kártya-szegély adja a tagolást. */}
      <div className="text-sm leading-[1.65]">{children}</div>
    </div>
  );
}
